import { memo, useMemo } from 'react';
import { Select } from '@gupshup-frontend/gupshup-ts-components';

const FIELD_TEST_ID = 'bloomreach-message-template-switcher';
// An option of the open menu. The search row is a role=option too, but it has no data-value.
const OPTION = '[role="option"][data-value]';

const joined = (...parts) => parts.filter(Boolean).join(' · ');
const countOf = (labels) => labels.reduce((counts, label) => counts.set(label, (counts.get(label) || 0) + 1), new Map());

// The dropdown's items, in the list's order. A name two templates share (one per language, say) reads `name · language`,
// and `· category` too where that is still the same. A unique name stays plain.
export function switcherItems(templates) {
  const names = countOf(templates.map(({ details }) => details.name));
  const withLanguage = templates.map(({ details }) => (names.get(details.name) > 1 ? joined(details.name, details.language) : details.name));
  const labels = countOf(withLanguage);
  return templates.map(({ details }, index) => ({
    value: String(details.id),
    label: labels.get(withLanguage[index]) > 1 ? joined(withLanguage[index], details.category) : withLanguage[index],
  }));
}

// The kit's search row stops every key but Escape, and MUI's list moves focus between sibling rows, which the search box
// is not one of. So no key gets from the box to an option, and ArrowUp off the first option, like Home, lands on the
// search row instead. The menu is in a portal, but React still sends its keys up through the Select: this takes them on
// the way down (capture), before the box swallows them, and does only what the kit leaves undone. The rest (ArrowUp and
// ArrowDown between options, End, Enter on an option, Escape) stays the kit's.
function onMenuKeyDownCapture(event) {
  const { key, target } = event;
  const list = target.closest('[role="listbox"]');
  const search = list?.querySelector('input');
  const first = list?.querySelector(OPTION);
  // Off the closed field, and off a menu with no option to move to. A modified key is a shortcut, not a move.
  if (!search || !first || event.altKey || event.ctrlKey || event.metaKey || event.nativeEvent.isComposing) return;

  // A pick happens once. A held key's repeats come after the kit has reset its list, so they must not pick from it.
  let move = null;
  if (target === search) {
    if (key === 'ArrowDown') move = () => first.focus();
    // The menu opens with the cursor in an empty box, so an Enter there is the second press of "open it", not a pick.
    if (key === 'Enter' && !event.repeat && search.value.trim()) move = () => first.click();
  } else if (target.matches(OPTION)) {
    if (key === 'ArrowUp' && target === first) move = () => search.focus();
    if (key === 'Home') move = () => first.focus();
    // The Select cancels MUI's own click on Space, so it picks nothing without this. Enter on an option is MUI's.
    if (key === ' ' && !event.repeat) move = () => target.click();
  }
  if (!move) return;
  event.preventDefault();
  event.stopPropagation();
  move();
}

// The open template's name as a dropdown over the current channel's templates, in the context bar in place of the
// plain name. Picking another one is Message's switch (handleSwitch): it carries the values and opens that template
// with no list in between. onChange gets the picked template's id. It sits outside Message's composer wrapper, so the
// double-brace watch never sees the Select's hidden input, which holds the template id.
// Memoized on the template list, and Message hands it a stable onChange: a keystroke elsewhere redraws none of it.
const TemplateSwitcher = memo(function TemplateSwitcher({ templates, value, onChange }) {
  const items = useMemo(() => switcherItems(templates), [templates]);
  return (
    <div className="br-template-switcher">
      <Select
        id="br-template-switcher"
        // Named for assistive tech; the bar already says what it is, so the visible label is hidden (index.css).
        label="Template"
        size="small"
        width="100%"
        showSearch
        searchPlaceholder="Search templates"
        searchBoxTestId={`${FIELD_TEST_ID}-search`}
        // Bloomreach's iframe is ~620px tall: a taller menu slides up over the field.
        maxHeightOfDropdown="240px"
        items={items}
        value={String(value)}
        handleChange={onChange}
        onKeyDownCapture={onMenuKeyDownCapture}
        SelectDisplayProps={{ 'data-testid': FIELD_TEST_ID }}
      />
    </div>
  );
});

export default TemplateSwitcher;
